import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "taxflow_token";

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Gracefully handle an expired/invalid staff session: clear the token and send the
// user to login instead of surfacing an uncaught 401 runtime-error overlay.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const path = window.location.pathname;
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/me");
    if (status === 401 && !isAuthCall && !path.startsWith("/login") && !path.startsWith("/portal")) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.assign("/login");
    }
    return Promise.reject(error);
  }
);

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    // Defense-in-depth: when a staff session starts, the portal token is no longer relevant
    // in this browser context. Clearing it prevents a stale portal token from being
    // submitted to portal endpoints by accident.
    localStorage.removeItem("taxflow_portal_token");
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  // Pydantic / FastAPI validation error array: [{type, loc, msg, ...}, ...]
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
      .filter(Boolean)
      .join(" ") || "Validation failed. Please check all fields.";
  // Structured error object — extract the most human-readable field in priority order
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.error === "string") return detail.error;
    if (typeof detail.detail === "string") return detail.detail;
    // Last resort: safe JSON so we never show [object Object]
    try { return JSON.stringify(detail); } catch { return "Unexpected error response."; }
  }
  return "Something went wrong. Please try again.";
}

// ── Phase 4: Lifecycle Reconciliation Queue / LiveOps Management ──────────────
export const getLifecycleQueueHealth = () =>
  api.get("/admin/lifecycle-queue-health");

export const getOperationalIncidents = (params = {}) =>
  api.get("/admin/operational-incidents", { params });

export const acknowledgeIncident = (incidentId) =>
  api.post(`/admin/operational-incidents/${incidentId}/acknowledge`);

export const retryIncident = (incidentId) =>
  api.post(`/admin/operational-incidents/${incidentId}/retry`);
